import React, { useContext, useEffect, useState, useCallback } from "react";
import ContentLayout from "../../component/ContentLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import axios from "../../util/axios";
import moment from "moment";
import { Context } from "../../component/Context/ErrorContext";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

const Referrals = () => {
    const { setError } = useContext(Context);
    const [referralData, setReferralData] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Filters
    const [filters, setFilters] = useState({
        full_name: "",
        company_name: "",
        email: "",
    });

    const [colDefs] = useState<any>([
        {
            field: "full_name",
            headerName: "Referrer Name",
            minWidth: 180,
        },
        {
            field: "email",
            headerName: "Referrer Email",
            minWidth: 200,
        },
        {
            field: "company_name",
            headerName: "Business Name",
            minWidth: 200,
        },
        {
            field: "referred_by_name",
            headerName: "Referred Person",
            minWidth: 180,
        },
        {
            field: "phone",
            headerName: "Phone",
            minWidth: 150,
        },
        {
            field: "number_of_employees",
            headerName: "Employees",
            minWidth: 120,
        },
        {
            field: "role",
            headerName: "Role",
            minWidth: 150,
        },
        {
            field: "created_dt",
            headerName: "Created Date",
            minWidth: 150,
            cellRenderer: (param: any) => {
                return (
                    <div className={"h-full flex flex-col items-center justify-center text-sm"}>
                        <p className={"p-0"}>
                            {moment(param.value).format("MM-DD-yyyy")}
                        </p>
                        <p className={"p-0 font-bold"}>
                            {moment(param.value).format("hh:mm A")}
                        </p>
                    </div>
                );
            },
        },
    ]);

    const fetchReferralData = useCallback(async () => {
        setLoading(true);
        const params = new URLSearchParams();
        params.append("page_num", pageNumber.toString());
        params.append("page_size", pageSize.toString());
        if (filters.full_name) params.append("full_name", filters.full_name);
        if (filters.company_name) params.append("company_name", filters.company_name);
        if (filters.email) params.append("email", filters.email);

        axios({
            URL: `/omw/admin/get-company-referrals?${params.toString()}`,
            method: "GET",
        })
            .then((res: any) => {
                if (res?.data?.status) {
                    setReferralData(res?.data?.data?.referrals || []);
                    setTotalRecords(res?.data?.data?.total || 0);
                } else {
                    setError(res?.data?.message || "Failed to fetch referrals");
                }
            })
            .catch((err) => setError(err?.response?.data?.message || "Something went wrong"))
            .finally(() => setLoading(false));
    }, [pageNumber, pageSize, filters, setError]);

    useEffect(() => {
        fetchReferralData();
    }, [fetchReferralData]);

    const onPageChange = (action: string, step: number) => {
        if (action === "prev") {
            if (pageNumber - step > 0) {
                setPageNumber(pageNumber - step);
            } else {
                setPageNumber(1);
            }
        } else {
            const totalPages = Math.ceil(totalRecords / pageSize);
            if (pageNumber + step <= totalPages) {
                setPageNumber(pageNumber + step);
            } else {
                setPageNumber(totalPages);
            }
        }
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
        setPageNumber(1);
    };

    const clearFilters = () => {
        setFilters({
            full_name: "",
            company_name: "",
            email: "",
        });
        setPageNumber(1);
    };

    return (
        <ContentLayout className={"items-start"}>
            <p className={"text-3xl font-bold text-secondary"}>Company Referrals</p>

            <div className={"mt-4 w-full h-auto bg-white p-2"}>
                {/* Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 p-3">
                    <input
                        className="border border-primary border-solid p-2 text-primary placeholder-primary rounded-md w-full"
                        placeholder="Referrer Name"
                        name="full_name"
                        value={filters.full_name}
                        onChange={handleFilterChange}
                    />
                    <input
                        className="border border-primary border-solid p-2 text-primary placeholder-primary rounded-md w-full"
                        placeholder="Business Name"
                        name="company_name"
                        value={filters.company_name}
                        onChange={handleFilterChange}
                    />
                    <input
                        className="border border-primary border-solid p-2 text-primary placeholder-primary rounded-md w-full"
                        placeholder="Referrer Email"
                        name="email"
                        value={filters.email}
                        onChange={handleFilterChange}
                    />
                    <div className="flex gap-2">
                        <button
                            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50"
                            onClick={() => fetchReferralData()}
                            disabled={loading}
                        >
                            Search
                        </button>
                        <button
                            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50"
                            onClick={clearFilters}
                            disabled={loading}
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="ag-theme-quartz w-full" style={{ height: 500 }}>
                    <AgGridReact
                        loading={loading}
                        rowData={referralData}
                        columnDefs={colDefs}
                        defaultColDef={{
                            flex: 1,
                            minWidth: 100,
                            resizable: true,
                        }}
                    />
                </div>

                {/* Pagination */}
                <div className="my-2 px-2 w-full flex justify-end flex-col sm:flex-row gap-4 sm:gap-10">
                    <div>
                        <label htmlFor="pageSize">Page size:</label>
                        <select
                            id="pageSize"
                            className="w-[100px] border-b border-primary border-solid p-2 outline-none ml-2"
                            value={pageSize}
                            onChange={(e) => {
                                setPageNumber(1);
                                setPageSize(+e.target.value);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex gap-2 justify-center items-center">
                        <KeyboardDoubleArrowLeftIcon
                            className="text-lg cursor-pointer"
                            onClick={() => (pageNumber < 2 ? null : onPageChange("prev", 2))}
                        />
                        <KeyboardArrowLeftIcon
                            className="text-lg cursor-pointer"
                            onClick={() => (pageNumber < 2 ? null : onPageChange("prev", 1))}
                        />
                        <p>
                            Page {pageNumber} of {Math.max(1, Math.ceil(totalRecords / pageSize))}
                        </p>
                        <KeyboardArrowRightIcon
                            className="text-lg cursor-pointer"
                            onClick={() =>
                                pageNumber >= Math.ceil(totalRecords / pageSize)
                                    ? null
                                    : onPageChange("next", 1)
                            }
                        />
                        <KeyboardDoubleArrowRightIcon
                            className="text-lg cursor-pointer"
                            onClick={() =>
                                pageNumber + 1 >= Math.ceil(totalRecords / pageSize)
                                    ? null
                                    : onPageChange("next", 2)
                            }
                        />
                    </div>
                </div>
                <div className="px-3 pb-3 text-sm text-gray-500">
                    Total Records: {totalRecords}
                </div>
            </div>
        </ContentLayout>
    );
};

export default Referrals;
